/**
 * Admin → Verify memberships → Newcomers: everyone who has joined Otable, as a cumulative line over time,
 * with a marker at your last visit and the latest sign-ups. Opening the tab marks it as seen, so the
 * chip on the tab counts people who joined since then (see VerifyMembershipsPanel).
 * Placeholder (external) and generated accounts are not counted. Backend: GET/POST /users/admin-newcomers/.
 */
import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

const W = 900;
const H = 280;
const PAD = { l: 48, r: 16, t: 16, b: 32 };

const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

/** Four even, whole-number steps from 0 that reach at least v. */
function niceStep(v) {
  const raw = Math.max(1, Math.ceil(v / 4));
  const mag = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((u) => u >= raw && Number.isInteger(u)) || raw;
  return unit;
}

function CumulativeChart({ points, lastVisit }) {
  const theme = useTheme();
  const [hover, setHover] = useState(null);
  const line = theme.palette.primary.main;
  const ink = theme.palette.text.secondary;
  const grid = theme.palette.divider;

  const { x, y, path, area, ticks, months, visitX } = useMemo(() => {
    const n = points.length;
    const step = niceStep(points[n - 1]?.total || 0);
    const max = step * 4;
    const xf = (i) => PAD.l + (n <= 1 ? 0 : (i / (n - 1)) * (W - PAD.l - PAD.r));
    const yf = (v) => H - PAD.b - (v / max) * (H - PAD.t - PAD.b);
    const d = points.map((p, i) => `${i ? 'L' : 'M'}${xf(i).toFixed(1)},${yf(p.total).toFixed(1)}`).join('');
    const a = n ? `${d}L${xf(n - 1).toFixed(1)},${yf(0)}L${xf(0).toFixed(1)},${yf(0)}Z` : '';
    const t = [0, 1, 2, 3, 4].map((k) => k * step);
    const m = [];
    let last = -99;
    points.forEach((p, i) => {
      if (p.date.endsWith('-01') || i === 0) {
        if (xf(i) - last > 56) { m.push({ i, label: new Date(`${p.date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) }); last = xf(i); }
      }
    });
    let vx = null;
    if (lastVisit) {
      const day = lastVisit.slice(0, 10);
      const idx = points.findIndex((p) => p.date >= day);
      if (idx >= 0) vx = xf(idx);
    }
    return { x: xf, y: yf, path: d, area: a, ticks: t, months: m, visitX: vx };
  }, [points, lastVisit]);

  const onMove = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    const n = points.length;
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - PAD.l) / (W - PAD.l - PAD.r)) * (n - 1))));
    setHover(i);
  };
  const h = hover != null ? points[hover] : null;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label={`Otable members over time: ${points[points.length - 1]?.total || 0} in total`}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)}
        sx={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke={grid} strokeWidth="1" />
            <text x={PAD.l - 8} y={y(t) + 4} textAnchor="end" fontSize="12" fill={ink}>{t}</text>
          </g>
        ))}
        {months.map((m) => (
          <text key={m.i} x={x(m.i)} y={H - 10} textAnchor="middle" fontSize="12" fill={ink}>{m.label}</text>
        ))}
        <path d={area} fill={line} opacity="0.1" />
        <path d={path} fill="none" stroke={line} strokeWidth="2" strokeLinejoin="round" />
        {visitX != null && (
          <g>
            <line x1={visitX} x2={visitX} y1={PAD.t} y2={H - PAD.b} stroke={ink} strokeWidth="1" strokeDasharray="4 4" />
            <text x={visitX - 6} y={PAD.t + 10} textAnchor="end" fontSize="12" fill={ink}>your last visit</text>
          </g>
        )}
        {h && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={H - PAD.b} stroke={theme.palette.text.primary} strokeWidth="1" opacity="0.35" />
            <circle cx={x(hover)} cy={y(h.total)} r="5" fill={line} stroke={theme.palette.background.paper} strokeWidth="2" />
          </g>
        )}
      </Box>
      <Typography aria-live="polite" sx={{ fontSize: 13, mt: 0.5, minHeight: 20, color: h ? 'text.primary' : 'text.secondary' }}>
        {h ? `${fmtDay(h.date)} · ${h.total.toLocaleString()} members · ${h.joined ? `${h.joined} joined that day` : 'nobody joined that day'}`
          : 'Hover the line to see a day.'}
      </Typography>
      <Box sx={visuallyHidden}>
        <table>
          <caption>Members on Otable by day</caption>
          <thead><tr><th>Day</th><th>Joined</th><th>Total</th></tr></thead>
          <tbody>{points.filter((p) => p.joined).map((p) => <tr key={p.date}><td>{p.date}</td><td>{p.joined}</td><td>{p.total}</td></tr>)}</tbody>
        </table>
      </Box>
    </Box>
  );
}

function Stat({ label, value, note }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 160, borderRadius: 2 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
      {note && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{note}</Typography>}
    </Paper>
  );
}

export default function NewcomersTab({ data, error, lastVisit }) {
  if (error) return <Alert severity="error">Newcomers could not be loaded.</Alert>;
  if (!data) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  const pts = data.points || [];
  const last30 = pts.slice(-30).reduce((s, p) => s + p.joined, 0);
  const before30 = pts.slice(-60, -30).reduce((s, p) => s + p.joined, 0);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2.5}>
        <Stat label="Members on Otable" value={data.total.toLocaleString()} />
        <Stat label="Joined in the last 30 days" value={last30.toLocaleString()}
          note={pts.length > 30 ? `${before30.toLocaleString()} in the 30 days before` : null} />
        <Stat label="New since your last visit" value={data.since_last_visit.toLocaleString()}
          note={lastVisit ? `last visit ${fmtWhen(lastVisit)}` : 'first visit to this tab'} />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Members over time</Typography>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.5 }}>
          Everyone who has signed up, added up day by day (placeholder and generated accounts are not counted).
        </Typography>
        {pts.length ? <CumulativeChart points={pts} lastVisit={lastVisit} /> : <Alert severity="info">Nobody has joined yet.</Alert>}
      </Paper>

      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>Latest sign-ups</Typography>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Joined</TableCell><TableCell>Email confirmed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(data.recent || []).map((u) => {
            const isNew = !lastVisit || u.created > lastVisit;
            return (
              <TableRow key={u.id} sx={isNew ? { bgcolor: 'primary.50' } : undefined}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {u.name}
                    {isNew && <Chip size="small" label="New" color="primary" sx={{ height: 18, fontSize: 11 }} />}
                    {!u.is_active && <Chip size="small" label="Inactive" sx={{ height: 18, fontSize: 11 }} />}
                  </Box>
                </TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{fmtWhen(u.created)}</TableCell>
                <TableCell>{u.email_verified ? 'Yes' : 'Not yet'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
